import { keyframes } from '@emotion/react';
import {
  Body,
  Box,
  Grid,
  HStack,
  Icon,
  motion,
  useBreakpointValue,
} from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { getSocialOptions } from 'src/modules/app/components/Layout/Footer/Social/SocialLinkProps';
import { LinkExternal } from 'src/shared/components';
import type { LinkExternalProps } from 'src/shared/components/LinkExternal';
import { useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { External } from 'src/shared/route';

import { mobileNavbarHeight } from '../Navbar';

export const desktopFooterHeight = 100;
export const mobileFooterHeight = 250;

interface iWindow {
  OneTrust: {
    ToggleInfoDisplay: () => void;
  };
}

interface FooterProps {
  theme?: 'light' | 'dark';
}

const templateAreas = {
  base: `
  'links'
  'socialLinks'
  'copyRight'
  `,
  tablet: `'copyRight links socialLinks'`,
};

const templateColumns = {
  base: '1fr',
  tablet: 'auto 1fr auto',
};

const templateRows = {
  base: '1fr auto 1fr',
  tablet: 'auto',
};

const MotionGrid = motion(Grid);

const FooterLink = (props: LinkExternalProps) => (
  <LinkExternal
    _active={{ color: 'inherit', textDecorationColor: 'inherit' }}
    color="inherit"
    {...props}
  />
);

const Footer = ({ theme = 'light' }: FooterProps) => {
  const [bottom, setBottom] = useState(`-${desktopFooterHeight}px`);

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const colorTheme = theme === 'light' ? 'neutral.0' : 'neutral.900';

  const handleCookieSettingsClick = () => {
    (window as unknown as iWindow).OneTrust.ToggleInfoDisplay();
  };

  const isMobile = useBreakpointValue(
    { base: true, tablet: false },
    { fallback: 'tablet' },
  );

  const opacityAnimation = keyframes`
    0% { opacity: 0 };
    50% { opacity: 0 };
    100% { opacity: 1 }
  `;

  useEffect(() => {
    const calcBottom = () => {
      if (typeof window === 'undefined') return;

      if (isMobile) {
        setBottom('0px');
      } else {
        const diffGridWindow = 500 + desktopFooterHeight - window.innerHeight;
        setBottom(
          `-${
            desktopFooterHeight + (diffGridWindow > 0 ? diffGridWindow : 0)
          }px`,
        );
      }
    };

    if (typeof window !== 'undefined') {
      calcBottom();
    }

    window.addEventListener('resize', calcBottom);

    return () => {
      window.removeEventListener('resize', calcBottom);
    };
  }, [isMobile]);

  return (
    <MotionGrid
      animation={`${opacityAnimation} 1s ease-out`}
      backgroundColor="rgba(0, 0, 0, 0.1)"
      bottom={bottom}
      color={colorTheme}
      fill={colorTheme}
      minHeight={{
        base: `${mobileFooterHeight + mobileNavbarHeight}px`,
        tablet: `${desktopFooterHeight}px`,
      }}
      mt="0 !important"
      position={!isMobile ? 'absolute' : 'relative'}
      templateAreas={templateAreas}
      templateColumns={templateColumns}
      templateRows={templateRows}
      width="100%"
    >
      <Box
        alignItems="center"
        flexDirection={{ base: 'column', tablet: 'row' }}
        gridArea="links"
        justifyContent={{ base: 'center', tablet: 'flex-start' }}
        width="100%"
      >
        <HStack alignItems="center" justifyContent="center" mx={200}>
          <Body color="inherit" fontSize={200} mx={300}>
            <Trans
              components={{
                PrivacyPolicyLink: (
                  <FooterLink href={External.PRIVACY_POLICY.path} />
                ),
                TermsOfUSeLink: (
                  <FooterLink href={External.TERMS_OF_SERVICE.path} />
                ),
              }}
              i18nKey="footer.privacyAndTermsOfUse"
              t={t}
            />
          </Body>
          <FooterLink
            as="button"
            color="inherit"
            cursor="pointer"
            fontSize={200}
            mx={200}
            onClick={handleCookieSettingsClick}
          >
            {t('footer.cookieSettings')}
          </FooterLink>
        </HStack>
        <Box justifyContent={{ base: 'center', tablet: 'flex-start' }}>
          <Body color="inherit" fontSize={200} mx={300}>
            <Trans
              components={{
                GenerationModelLink: (
                  <FooterLink href={External.MODEL_LICENSE.path} />
                ),
                CreatorLink: <FooterLink href={External.MODEL_CREATOR.path} />,
              }}
              i18nKey="footer.generationAndCreator"
              t={t}
            />
          </Body>
        </Box>
      </Box>
      <Box
        gridArea="socialLinks"
        justifyContent="center"
        pr={{ base: 0, tablet: 700 }}
        width="100%"
      >
        {getSocialOptions(t).map(({ label, icon, path, color, fill }) => (
          <FooterLink color={color} href={path} key={label} mx={200}>
            <Icon fill={fill} icon={icon as IconProp} size="medium" />
          </FooterLink>
        ))}
      </Box>
      <Box
        alignItems="center"
        gridArea="copyRight"
        justifyContent="center"
        mx={{ base: 0, tablet: 200 }}
        pb={{ base: `${mobileNavbarHeight}px`, tablet: '0' }}
        pl={{ base: 0, tablet: 500 }}
        width="100%"
      >
        <Body color="inherit" fontSize={200} mx={300}>
          {t('footer.copyRight', { year: new Date().getFullYear() })}
        </Body>
      </Box>
    </MotionGrid>
  );
};

export { Footer, getSocialOptions };
